import { Journey } from 'features/catalog/badge-journeys';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';

export interface FeedUpdate {
  profile: Profile;
  /** Timestamp of this specific feed event (submission), not necessarily profile.lastUpdated. */
  eventAt: Date;
  newBadges: string[];
  badgesByRarity: {
    mythic: Badge[];
    legendary: Badge[];
    rare: Badge[];
    common: Badge[];
  };
  /** All new badges sorted rarest-first — avoids re-sorting on every card render. */
  sortedNewBadges: Badge[];
  isNewUser: boolean;
  /** Catalog drop — new badges added to the app (not a person claim). */
  isCatalogAddition?: boolean;
  /** System grant — contribution awards and every `banSelfClaim` badge always use
   * “granted” wording (even if the id also appears on a sheet row).
   */
  isAutoGrant?: boolean;
  /**
   * Recipients when the same auto-grant landed for multiple people at the same moment
   * (same badge set + timestamp). Includes `profile`; omit / single-entry = one person.
   */
  grantedToProfiles?: Profile[];
  /** Journeys newly completed by this submission. */
  completedJourneys: Journey[];
  positionsMovedUp?: number;
}

/** Stable id for matching aside activity → visible feed cards. */
export const feedUpdateKey = (update: FeedUpdate): string => {
  if (update.isCatalogAddition) {
    return `catalog|${update.eventAt.getTime()}|${update.newBadges.join(',')}`;
  }
  const recipients = update.grantedToProfiles;
  if (recipients && recipients.length > 1) {
    const emails = recipients
      .map((p) => p.email)
      .sort()
      .join(',');
    return `grant|${update.eventAt.getTime()}|${[...update.newBadges].sort().join(',')}|${emails}`;
  }
  return `${update.profile.email}|${update.eventAt.getTime()}|${update.newBadges.join(',')}`;
};

/** Synthetic actor for catalog “new badges added” cards. */
export const CATALOG_FEED_PROFILE: Profile = {
  email: 'talos-badges@system',
  name: 'Talos Badges',
  lastUpdated: new Date(0),
  lastActivityAt: new Date(0),
  submittedBadgeIDs: [],
  badgesIDs: [],
  sortScore: 0,
  journeyEmojis: [],
};

/** True when idea-credits include this profile local-part. */
export const badgeCreditedToLocal = (badge: Badge, localPart: string): boolean => {
  if (!badge.credits) return false;
  const re = /([^\s,@]+)@/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(badge.credits)) !== null) {
    if (match[1] === localPart) return true;
  }
  return false;
};
